import { useState } from 'react';
import {
  Sliders,
  Monitor,
  Smartphone,
  ShoppingCart,
  CreditCard,
  Gift,
  Image,
  Columns2,
  Square,
  LayoutGrid,
  Maximize2,
  GalleryHorizontalEnd,
  Check,
  Tag,
  Eye,
  MousePointerClick,
  ListChecks,
  Image as ImageIcon,
  Sparkles,
  Layers,
  Type,
  FormInput,
  MessageSquare,
  Layout,
  FileText,
  Upload,
  X,
  Plus,
  Trash2,
} from 'lucide-react';
import type { KreatorSettingsData } from '../KreatorPage';
import { SettingsSaveBar } from '../../components/SettingsSaveBar';
import { SectionHeader } from '../../components/SectionHeader';
import { BreakpointSlider, Slider, SliderCard } from '../../components/ui/Slider';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { WpMediaPickerModal } from '../../components/WpMediaPickerModal';
import { createTenantKreatorAdapter, type KreatorApiAdapter } from '../../lib/kreator-api-adapter';
import { normalizeHexColor, prefixHexColor, safeColorForPicker } from './builder-v2/utils/color-utils';
import { KreatorSettingsSection } from './KreatorSettingsSection';
import { WcMultiProductPicker } from './funnel/components/WcMultiProductPicker';
import { IconPickerField } from './builder-v2/fields/IconPickerField';
import { CheckoutFieldsEditor } from './funnel/components/CheckoutFieldsEditor';
import type { CheckoutFieldConfig } from '../../types/funnel.types';

interface Props {
  integrationId: string;
  settings: KreatorSettingsData | null;
  onSaved: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  apiAdapter?: KreatorApiAdapter;
}

type TabKey = 'general' | 'pages' | 'productPage' | 'cart' | 'checkout' | 'thankYouPage' | 'shopArchive';

const TABS = [
  { key: 'general' as TabKey, name: 'Ogólne', icon: Sliders },
  { key: 'pages' as TabKey, name: 'Strony', icon: FileText },
  { key: 'productPage' as TabKey, name: 'Strona produktu', icon: Monitor },
  { key: 'cart' as TabKey, name: 'Koszyk', icon: ShoppingCart },
  { key: 'checkout' as TabKey, name: 'Kasa', icon: CreditCard },
  { key: 'thankYouPage' as TabKey, name: 'Podziękowanie', icon: Gift },
  { key: 'shopArchive' as TabKey, name: 'Sklep', icon: LayoutGrid },
];

type GalleryType = 'default' | 'classic' | 'vertical-scroll' | 'full-width';

const GALLERY_TYPES: readonly { value: GalleryType; name: string; description: string; icon: typeof Image }[] = [
  {
    value: 'classic',
    name: 'Klasyczna EcomFlow',
    description: 'Duży obraz z rzędem miniaturek poniżej. Na mobile: obraz na pełną szerokość.',
    icon: GalleryHorizontalEnd,
  },
  {
    value: 'vertical-scroll',
    name: 'Pionowa EcomFlow',
    description: 'Wszystkie zdjęcia ułożone jedno pod drugim z miniaturami po lewej. Opis produktu podąża przy przewijaniu.',
    icon: Columns2,
  },
  {
    value: 'full-width',
    name: 'Pełna szerokość',
    description: 'Galeria na pełną szerokość nad opisem produktu.',
    icon: Maximize2,
  },
  {
    value: 'default',
    name: 'Klasyczna WooCommerce',
    description: 'Standardowa galeria WooCommerce z suwakiem i miniaturkami.',
    icon: Image,
  },
] as const;

const BUTTON_STYLES = [
  { value: 'filled', label: 'Wypełniony' },
  { value: 'outline', label: 'Obrys' },
] as const;

const TABS_STYLES = [
  { value: 'default', label: 'Standardowe' },
  { value: 'integrated', label: 'Zintegrowane' },
] as const;

const CART_LAYOUTS = [
  { value: 'standard', label: 'Standardowy', description: 'Klasyczny układ koszyka' },
  { value: 'compact', label: 'Kompaktowy', description: 'Wysuwany - interaktywny koszyk' },
] as const;

const CART_CTA_ICONS = [
  { value: 'arrow-right', label: 'Strzałka' },
  { value: 'lock', label: 'Kłódka' },
  { value: 'shopping-bag', label: 'Torba zakupowa' },
  { value: 'credit-card', label: 'Karta płatnicza' },
  { value: 'none', label: 'Bez ikony' },
] as const;

const CROSS_SELL_BADGE_BORDER_STYLES = [
  { value: 'none', label: 'Bez ramki' },
  { value: 'solid', label: 'Ciągła' },
  { value: 'dashed', label: 'Przerywana' },
  { value: 'dotted', label: 'Kropkowana' },
] as const;

const CART_BENEFIT_ICONS = [
  { value: 'shield', label: 'Tarcza' },
  { value: 'truck', label: 'Dostawa' },
  { value: 'return', label: 'Zwrot' },
  { value: 'lock', label: 'Kłódka' },
  { value: 'star', label: 'Gwiazdka' },
  { value: 'check', label: 'Check' },
] as const;

const CHECKOUT_LAYOUTS = [
  { value: 'delivery-first', label: 'Dostawa przed danymi', description: 'Zamówienie → Dostawa → Dane → Płatność' },
  { value: 'standard', label: 'Standardowy', description: 'Wszystko na jednej stronie' },
  { value: 'multi-step', label: 'Wielokrokowy', description: 'Dostawa → Płatność → Podsumowanie' },
  { value: 'one-page', label: 'Jednoekranowy', description: 'Zoptymalizowany kompaktowy widok' },
] as const;

const PRODUCT_CARD_STYLES = [
  { value: 'default', label: 'Domyślny', description: 'Zdjęcie, nazwa, cena, przycisk' },
  { value: 'minimal', label: 'Minimalny', description: 'Zdjęcie, nazwa i cena' },
  { value: 'detailed', label: 'Szczegółowy', description: 'Z oceną i krótkim opisem' },
] as const;

const PAGE_SECTION_TYPES = [
  { value: 'contained', label: 'W kontenerze' },
  { value: 'full-stretch', label: 'Pełna szerokość' },
] as const;

const PAGE_RADIUS_MODES = [
  { value: 'theme', label: 'Domyślne' },
  { value: 'custom', label: 'Własne' },
] as const;

const IMAGE_RATIOS = [
  { value: '1:1', label: 'Kwadrat (1:1)' },
  { value: '4:3', label: 'Poziomy (4:3)' },
  { value: '3:4', label: 'Pionowy (3:4)' },
  { value: '16:9', label: 'Panorama (16:9)' },
] as const;

const HOVER_EFFECTS = [
  { value: 'none', label: 'Brak' },
  { value: 'zoom', label: 'Powiększenie' },
  { value: 'swap', label: 'Zmiana zdjęcia' },
] as const;

// ─── Badge Color Picker (with "Default" as null) ────────────────────────────

function BadgeColorPicker({ value, onChange, colors, defaultLabel }: {
  value: string | null;
  onChange: (v: string | null) => void;
  colors: { primary: string; additional1: string; additional2: string; additional3: string };
  defaultLabel?: string;
}) {
  const palette = [
    { label: defaultLabel || 'Domyślny', hex: null, storeValue: null as string | null },
    { label: 'Główny', hex: colors.primary, storeValue: colors.primary },
    { label: 'Dodatkowy #1', hex: colors.additional1, storeValue: colors.additional1 },
    { label: 'Dodatkowy #2', hex: colors.additional2, storeValue: colors.additional2 },
    { label: 'Dodatkowy #3', hex: colors.additional3, storeValue: colors.additional3 },
  ];
  const isCustom = value !== null && !palette.some(p => p.storeValue === value);
  const [showCustom, setShowCustom] = useState(isCustom);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {palette.map((p) => {
          const selected = value === p.storeValue;
          return (
            <button
              key={p.label}
              onClick={() => { onChange(p.storeValue); setShowCustom(false); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                selected && !showCustom
                  ? 'border-accent-primary/50 bg-accent-primary/10 text-accent-primary'
                  : 'border-border bg-surface-raised text-text-secondary hover:border-border-strong'
              }`}
            >
              {p.hex ? (
                <span className="w-4 h-4 rounded-full border border-border-strong flex-shrink-0 relative" style={{ backgroundColor: p.hex }}>
                  {selected && !showCustom && <Check className="w-3 h-3 text-text-primary absolute inset-0 m-auto drop-shadow" />}
                </span>
              ) : (
                <span className={`w-4 h-4 rounded-full border-2 border-dashed flex-shrink-0 relative ${
                  selected && !showCustom ? 'border-accent-primary' : 'border-gray-500'
                }`}>
                  {selected && !showCustom && <Check className="w-3 h-3 text-accent-primary absolute inset-0 m-auto" />}
                </span>
              )}
              {p.label}
            </button>
          );
        })}
        <button
          onClick={() => {
            setShowCustom(true);
            if (!isCustom) onChange('#3b82f6');
          }}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
            showCustom
              ? 'border-accent-primary/50 bg-accent-primary/10 text-accent-primary'
              : 'border-border bg-surface-raised text-text-secondary hover:border-border-strong'
          }`}
        >
          Własny
        </button>
      </div>
      {showCustom && (
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={safeColorForPicker(value, '#3b82f6')}
            onChange={(e) => onChange(e.target.value)}
            className="w-10 h-10 rounded-lg border border-border bg-transparent cursor-pointer"
          />
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(prefixHexColor(e.target.value) || null)}
            onBlur={(e) => onChange(normalizeHexColor(e.target.value) || null)}
            placeholder="#000000"
            className="input w-32 text-sm font-mono"
          />
        </div>
      )}
    </div>
  );
}

// ─── Toggle Component ────────────────────────────────────────────────────────

function Toggle({ checked, onChange, label, description, disabled = false }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between p-4 rounded-lg border border-border-subtle bg-surface-raised ${disabled ? 'opacity-75' : ''}`}>
      <div className="flex-1 mr-4">
        <div className="text-sm font-medium text-text-primary">{label}</div>
        {description && <div className="text-xs text-text-muted mt-0.5">{description}</div>}
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
          checked ? 'bg-accent-primary' : 'bg-surface-muted'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

// RangeSlider zastąpiony przez <SliderCard> z @/components/ui/Slider — jednolity
// markup w całym Kreatorze (label + value monospace u góry, suwak full-width).

// ─── Visual Card Select ──────────────────────────────────────────────────────

function CardSelect<T extends string>({ options, value, onChange }: {
  options: readonly { value: T; label: string; description?: string; icon?: typeof Monitor }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`p-4 rounded-lg border text-left transition-all ${
            value === opt.value
              ? 'border-accent-primary/50 bg-accent-primary/10'
              : 'border-border bg-surface-raised hover:border-border-strong'
          }`}
        >
          {opt.icon && <opt.icon className={`w-5 h-5 mb-2 ${value === opt.value ? 'text-accent-primary' : 'text-text-muted'}`} />}
          <div className="text-sm font-medium text-text-primary">{opt.label}</div>
          {opt.description && <div className="text-xs text-text-secondary mt-1">{opt.description}</div>}
        </button>
      ))}
    </div>
  );
}

// ─── Gallery Type Picker ─────────────────────────────────────────────────────

function GalleryTypePicker({ value, onChange }: {
  value: GalleryType;
  onChange: (v: GalleryType) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {GALLERY_TYPES.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`flex flex-col items-center text-center p-6 rounded-xl border-2 transition-all ${
            value === opt.value
              ? 'border-accent-primary bg-accent-primary/10 shadow-lg shadow-accent-primary/20/10'
              : 'border-border bg-surface-raised hover:border-border-strong hover:bg-surface-hover'
          }`}
        >
          <opt.icon className={`w-8 h-8 mb-3 ${value === opt.value ? 'text-accent-primary' : 'text-text-secondary'}`} />
          <div className={`text-sm font-semibold mb-1 ${value === opt.value ? 'text-accent-primary' : 'text-text-primary'}`}>
            {opt.name}
          </div>
          <div className="text-xs text-text-secondary leading-relaxed">{opt.description}</div>
        </button>
      ))}
    </div>
  );
}

// ─── Palette Color Picker ────────────────────────────────────────────────────

function PaletteColorPicker({ value, onChange, colors }: {
  value: string | null;
  onChange: (v: string | null) => void;
  colors: { primary: string; additional1: string; additional2: string; additional3: string };
}) {
  const palette = [
    { label: 'Główny', hex: colors.primary, storeValue: null as string | null },
    { label: 'Dodatkowy #1', hex: colors.additional1, storeValue: colors.additional1 },
    { label: 'Dodatkowy #2', hex: colors.additional2, storeValue: colors.additional2 },
    { label: 'Dodatkowy #3', hex: colors.additional3, storeValue: colors.additional3 },
  ];
  const isCustom = value !== null && !palette.some(p => p.storeValue === value);
  const [showCustom, setShowCustom] = useState(isCustom);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {palette.map((p) => {
          const selected = value === p.storeValue;
          return (
            <button
              key={p.label}
              onClick={() => { onChange(p.storeValue); setShowCustom(false); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                selected && !showCustom
                  ? 'border-accent-primary/50 bg-accent-primary/10 text-accent-primary'
                  : 'border-border bg-surface-raised text-text-secondary hover:border-border-strong'
              }`}
            >
              <span className="w-4 h-4 rounded-full border border-border-strong flex-shrink-0 relative" style={{ backgroundColor: p.hex }}>
                {selected && !showCustom && <Check className="w-3 h-3 text-text-primary absolute inset-0 m-auto drop-shadow" />}
              </span>
              {p.label}
            </button>
          );
        })}
        <button
          onClick={() => {
            setShowCustom(true);
            if (!isCustom) onChange('#3b82f6');
          }}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
            showCustom
              ? 'border-accent-primary/50 bg-accent-primary/10 text-accent-primary'
              : 'border-border bg-surface-raised text-text-secondary hover:border-border-strong'
          }`}
        >
          Własny
        </button>
      </div>
      {showCustom && (
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={safeColorForPicker(value, '#3b82f6')}
            onChange={(e) => onChange(e.target.value)}
            className="w-10 h-10 rounded-lg border border-border bg-transparent cursor-pointer"
          />
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(prefixHexColor(e.target.value) || null)}
            onBlur={(e) => onChange(normalizeHexColor(e.target.value) || null)}
            placeholder="#000000"
            className="input w-32 text-sm font-mono"
          />
        </div>
      )}
    </div>
  );
}

function HexColorInput({ label, value, onChange, fallback }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  fallback: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-text-secondary mb-2">{label}</label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={safeColorForPicker(value, fallback)}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-10 rounded-lg border border-border bg-transparent cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(prefixHexColor(e.target.value) || '')}
          onBlur={(e) => onChange(normalizeHexColor(e.target.value) || fallback)}
          placeholder={fallback}
          className="input w-32 text-sm font-mono"
        />
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function KreatorPageAppearanceSection({ integrationId, settings, onSaved, showError, apiAdapter }: Props) {
  const adapter = apiAdapter ?? createTenantKreatorAdapter(integrationId);
  const requestedTab = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('tab') : null;
  const [activeTab, setActiveTab] = useState<TabKey>(TABS.some(tab => tab.key === requestedTab) ? requestedTab as TabKey : 'general');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showPageBannerMediaPicker, setShowPageBannerMediaPicker] = useState(false);
  const typographyHref = adapter.isTenantScoped ? `/kreator/${integrationId}/typography` : null;

  const pa = settings?.pageAppearance;
  const paletteColors = {
    primary: settings?.colors?.primary || '#3b82f6',
    additional1: settings?.colors?.additional1 || '#10b981',
    additional2: settings?.colors?.additional2 || '#f59e0b',
    additional3: settings?.colors?.additional3 || '#ef4444',
  };

  const [form, setForm] = useState({
    pages: {
      bannerApplyMode: (pa?.pages?.bannerApplyMode || 'all') as 'all' | 'selected',
      bannerSectionType: (pa?.pages?.bannerSectionType || 'contained') as 'contained' | 'full-stretch',
      bannerBackgroundType: (pa?.pages?.bannerBackgroundType || 'color') as 'color' | 'image',
      bannerBackgroundColor: pa?.pages?.bannerBackgroundColor || '#f5f7fb',
      bannerBackgroundImageUrl: pa?.pages?.bannerBackgroundImageUrl || null,
      bannerTextColor: pa?.pages?.bannerTextColor || '#111827',
      bannerTextAlign: (pa?.pages?.bannerTextAlign || 'center') as 'left' | 'center' | 'right',
      bannerHeight: {
        desktop: pa?.pages?.bannerHeight?.desktop ?? 260,
        tablet: pa?.pages?.bannerHeight?.tablet ?? 220,
        mobile: pa?.pages?.bannerHeight?.mobile ?? 180,
      },
      bannerTopMargin: {
        desktop: pa?.pages?.bannerTopMargin?.desktop ?? 20,
        mobile: pa?.pages?.bannerTopMargin?.mobile ?? 20,
      },
      bannerRadiusMode: (pa?.pages?.bannerRadiusMode || 'theme') as 'theme' | 'custom',
      bannerBorderRadius: pa?.pages?.bannerBorderRadius ?? 8,
      showBreadcrumbs: pa?.pages?.showBreadcrumbs ?? true,
    },
    productPage: {
      galleryType: (pa?.productPage.galleryType || 'default') as GalleryType,
      columnRatio: pa?.productPage.columnRatio ?? 50,
      productPageColumnGap: pa?.productPage.productPageColumnGap ?? null,
      showRelatedProducts: pa?.productPage.showRelatedProducts ?? true,
      relatedProductsCount: pa?.productPage.relatedProductsCount ?? 4,
      showUpsells: pa?.productPage.showUpsells ?? true,
      showReviews: pa?.productPage.showReviews ?? true,
      showTabs: pa?.productPage.showTabs ?? true,
      tabsStyle: (pa?.productPage.tabsStyle === 'integrated' ? 'integrated' : 'default') as 'default' | 'integrated',
      addToCartButtonStyle: (pa?.productPage.addToCartButtonStyle === 'outline' ? 'outline' : 'filled') as 'filled' | 'outline',
      addToCartButtonColor: pa?.productPage.addToCartButtonColor || null,
      addToCartShowQuantity: pa?.productPage.addToCartShowQuantity ?? true,
      addToCartPulseEnabled: pa?.productPage.addToCartPulseEnabled ?? false,
      addToCartPulseColor: pa?.productPage.addToCartPulseColor || null,
      addToCartPulseOpacity: pa?.productPage.addToCartPulseOpacity ?? 0.4,
      showSku: pa?.productPage.showSku ?? false,
      showCategories: pa?.productPage.showCategories ?? true,
      stickyAddToCart: pa?.productPage.stickyAddToCart ?? false,
      stickyCartMode: pa?.productPage.stickyCartMode ?? 'direct',
      showOmnibusPrice: pa?.productPage.showOmnibusPrice ?? false,
      showSaleBadge: pa?.productPage.showSaleBadge ?? true,
      galleryLightbox: pa?.productPage.galleryLightbox ?? true,
      priceStyle: (pa?.productPage as Record<string, unknown>)?.priceStyle as 'default' | 'enhanced' || 'default',
      priceOldColor: (pa?.productPage as Record<string, unknown>)?.priceOldColor as string | null || null,
      priceNewColor: (pa?.productPage as Record<string, unknown>)?.priceNewColor as string | null || null,
      showDiscountBadge: (pa?.productPage as Record<string, unknown>)?.showDiscountBadge as boolean ?? true,
      discountBadgeBgColor: (pa?.productPage as Record<string, unknown>)?.discountBadgeBgColor as string | null || null,
      discountBadgeTextColor: (pa?.productPage as Record<string, unknown>)?.discountBadgeTextColor as string | null || null,
      showSavingsBadge: (pa?.productPage as Record<string, unknown>)?.showSavingsBadge as boolean ?? true,
      savingsBadgeBgColor: (pa?.productPage as Record<string, unknown>)?.savingsBadgeBgColor as string | null || null,
      savingsBadgeTextColor: (pa?.productPage as Record<string, unknown>)?.savingsBadgeTextColor as string | null || null,
      priceSeparator: (pa?.productPage as Record<string, unknown>)?.priceSeparator as boolean ?? false,
    },
    cart: {
      layout: (pa?.cart.layout === 'modern' ? 'compact' : pa?.cart.layout || 'standard') as 'standard' | 'compact',
      showCouponField: pa?.cart.showCouponField ?? true,
      showCrossSells: pa?.cart.showCrossSells ?? true,
      crossSellsCount: pa?.cart.crossSellsCount ?? 4,
      showContinueShopping: pa?.cart.showContinueShopping ?? true,
      continueShoppingUrl: pa?.cart.continueShoppingUrl || '',
      showEstimatedDelivery: pa?.cart.showEstimatedDelivery ?? false,
      showFreeShippingBar: pa?.cart.showFreeShippingBar ?? false,
      freeShippingThreshold: pa?.cart.freeShippingThreshold ?? null,
      autoUpdate: pa?.cart.autoUpdate ?? false,
      showWooCommerceNotices: pa?.cart.showWooCommerceNotices ?? pa?.shop?.showAddToCartNotice ?? true,
      showCartHeader: pa?.cart.showCartHeader ?? true,
      reservationEnabled: pa?.cart.reservationEnabled ?? true,
      reservationDurationMinutes: pa?.cart.reservationDurationMinutes ?? 10,
      reservationMessage: pa?.cart.reservationMessage || 'Rezerwacja produktów w Twoim koszyku przez {time}',
      reservationExpiredMessage: pa?.cart.reservationExpiredMessage === 'Czas rezerwacji minął. Produkty nadal czekają w koszyku.'
        ? 'Czas rezerwacji minął. Produkty czekają w koszyku.'
        : (pa?.cart.reservationExpiredMessage || 'Czas rezerwacji minął. Produkty czekają w koszyku.'),
      reservationHideExpired: pa?.cart.reservationHideExpired ?? false,
      reservationResetMinutes: pa?.cart.reservationResetMinutes ?? 30,
      compactFreeShippingEnabled: pa?.cart.compactFreeShippingEnabled ?? true,
      compactCrossSellsEnabled: pa?.cart.compactCrossSellsEnabled ?? true,
      compactCrossSellProductIds: pa?.cart.compactCrossSellProductIds ?? [],
      compactCrossSellBadge: {
        enabled: pa?.cart.compactCrossSellBadge?.enabled ?? false,
        text: pa?.cart.compactCrossSellBadge?.text || 'Mega obniżki tylko do tego zamówienia',
        backgroundColor: pa?.cart.compactCrossSellBadge?.backgroundColor ?? null,
        textColor: pa?.cart.compactCrossSellBadge?.textColor ?? null,
        borderColor: pa?.cart.compactCrossSellBadge?.borderColor ?? null,
        borderStyle: pa?.cart.compactCrossSellBadge?.borderStyle || 'solid' as const,
        borderWidth: pa?.cart.compactCrossSellBadge?.borderWidth ?? 1,
        borderRadius: pa?.cart.compactCrossSellBadge?.borderRadius ?? 12,
      },
      compactCostSummaryEnabled: pa?.cart.compactCostSummaryEnabled ?? true,
      compactCtaText: pa?.cart.compactCtaText || 'Przejdź do kasy',
      compactCtaIcon: pa?.cart.compactCtaIcon || 'arrow-right' as const,
      compactBenefits: pa?.cart.compactBenefits ?? [],
    },
    checkout: {
      layout: pa?.checkout.layout || 'standard' as const,
      showPromotion: pa?.checkout.showPromotion ?? true,
      promotionLabel: pa?.checkout.promotionLabel ?? 'Aktywna promocja',
      showCheckoutTimer: pa?.checkout.showCheckoutTimer ?? false,
      showTrustBadges: pa?.checkout.showTrustBadges ?? true,
      trustBadges: (pa?.checkout.trustBadges ?? [
        { icon: 'LockKeyhole', text: pa?.checkout.trustSecureText ?? 'Szyfrowane zakupy' },
        { icon: 'Truck', text: pa?.checkout.trustDispatchText ?? 'Szybka realizacja' },
        { icon: 'Store', text: pa?.checkout.trustSellerText ?? 'Polski sprzedawca' },
      ]).map((badge) => ({ ...badge, id: badge.id || crypto.randomUUID() })),

      returnsMessage: pa?.checkout.returnsMessage ?? '',
      simplifyPickupAddress: pa?.checkout.simplifyPickupAddress ?? false,
      pickupMapMode: pa?.checkout.pickupMapMode ?? 'plugins',
      showOrderNotes: pa?.checkout.showOrderNotes ?? true,
      showCompanyFields: pa?.checkout.showCompanyFields ?? false,
      showPhoneField: pa?.checkout.showPhoneField ?? true,
      showCouponField: pa?.checkout.showCouponField ?? true,
      stepLabels: {
        shipping: pa?.checkout.stepLabels?.shipping || 'Dostawa',
        payment: pa?.checkout.stepLabels?.payment || 'Płatność',
        review: pa?.checkout.stepLabels?.review || 'Podsumowanie',
      },
      termsCheckboxRequired: pa?.checkout.termsCheckboxRequired ?? true,
      termsAcceptance: {
        mode: pa?.checkout.termsAcceptance?.mode ?? 'checkbox' as 'checkbox' | 'cta',
        notice: pa?.checkout.termsAcceptance?.notice ?? 'Klikając przycisk zamówienia, akceptujesz {terms}. Informacje o przetwarzaniu danych znajdziesz w {privacy}.',
        termsLabel: pa?.checkout.termsAcceptance?.termsLabel ?? 'regulamin sklepu',
        termsUrl: pa?.checkout.termsAcceptance?.termsUrl || '/regulamin/',
        privacyLabel: pa?.checkout.termsAcceptance?.privacyLabel ?? 'polityce prywatności',
        privacyUrl: pa?.checkout.termsAcceptance?.privacyUrl || '/polityka-prywatnosci/',
        showCompanyInfo: pa?.checkout.termsAcceptance?.showCompanyInfo ?? false,
        companyInfo: pa?.checkout.termsAcceptance?.companyInfo ?? '',
        fontSize: pa?.checkout.termsAcceptance?.fontSize ?? 12,
        alignment: pa?.checkout.termsAcceptance?.alignment ?? 'left' as 'left' | 'center',
      },
      expressCheckoutEnabled: pa?.checkout.expressCheckoutEnabled ?? false,
      checkoutFields: pa?.checkout.checkoutFields ?? [] as CheckoutFieldConfig[],
    },
    thankYouPage: {
      customHeading: pa?.thankYouPage.customHeading || 'Dziękujemy za zamówienie!',
      customMessage: pa?.thankYouPage.customMessage || '',
      showOrderDetails: pa?.thankYouPage.showOrderDetails ?? true,
      showRecommendedProducts: pa?.thankYouPage.showRecommendedProducts ?? false,
      recommendedProductsCount: pa?.thankYouPage.recommendedProductsCount ?? 4,
      showReturnToShopButton: pa?.thankYouPage.showReturnToShopButton ?? true,
      returnToShopUrl: pa?.thankYouPage.returnToShopUrl || '',
      showNewsletter: pa?.thankYouPage.showNewsletter ?? false,
    },
    shopArchive: {
      columns: pa?.shopArchive?.columns ?? 4,
      productsPerPage: pa?.shopArchive?.productsPerPage ?? 12,
      showSorting: pa?.shopArchive?.showSorting ?? true,
      showResultCount: pa?.shopArchive?.showResultCount ?? true,
      showSidebar: pa?.shopArchive?.showSidebar ?? false,
      productCardStyle: pa?.shopArchive?.productCardStyle || 'default' as const,
      showAddToCartButton: pa?.shopArchive?.showAddToCartButton ?? true,
      showRating: pa?.shopArchive?.showRating ?? true,
      showSaleBadge: pa?.shopArchive?.showSaleBadge ?? true,
      imageAspectRatio: pa?.shopArchive?.imageAspectRatio || '1:1' as const,
      hoverEffect: pa?.shopArchive?.hoverEffect || 'none' as const,
    },
  });

  const updateProduct = (field: string, value: unknown) => {
    setForm({ ...form, productPage: { ...form.productPage, [field]: value } });
  };

  const updateCart = (field: string, value: unknown) => {
    setForm({ ...form, cart: { ...form.cart, [field]: value } });
  };

  const updateCrossSellBadge = (field: string, value: unknown) => {
    setForm({
      ...form,
      cart: {
        ...form.cart,
        compactCrossSellBadge: { ...form.cart.compactCrossSellBadge, [field]: value },
      },
    });
  };

  const addCartBenefit = () => {
    if (form.cart.compactBenefits.length >= 5) return;
    updateCart('compactBenefits', [...form.cart.compactBenefits, { icon: 'check' as const, text: 'Bezpieczne zakupy' }]);
  };

  const updateCartBenefit = (index: number, field: 'icon' | 'text', value: string) => {
    updateCart('compactBenefits', form.cart.compactBenefits.map((benefit, benefitIndex) => (
      benefitIndex === index ? { ...benefit, [field]: value } : benefit
    )));
  };

  const removeCartBenefit = (index: number) => {
    updateCart('compactBenefits', form.cart.compactBenefits.filter((_, benefitIndex) => benefitIndex !== index));
  };

  const updateCheckout = (field: string, value: unknown) => {
    setForm({ ...form, checkout: { ...form.checkout, [field]: value } });
  };

  const updateCheckoutLabels = (field: string, value: string) => {
    setForm({
      ...form,
      checkout: {
        ...form.checkout,
        stepLabels: { ...form.checkout.stepLabels, [field]: value },
      },
    });
  };

  const updateThankYou = (field: string, value: unknown) => {
    setForm({ ...form, thankYouPage: { ...form.thankYouPage, [field]: value } });
  };

  const updateShopArchive = (field: string, value: unknown) => {
    setForm({ ...form, shopArchive: { ...form.shopArchive, [field]: value } });
  };

  const updatePages = (field: string, value: unknown) => {
    setForm({ ...form, pages: { ...form.pages, [field]: value } });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adapter.updatePageAppearance(form);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      onSaved();
      if (adapter.isTenantScoped) {
        try {
          await adapter.purgeWpCache();
        } catch (purgeErr: unknown) {
          const msg = (purgeErr as { response?: { data?: { error?: string } } })?.response?.data?.error;
          try {
            const diag = await adapter.diagnoseWp();
            showError(diag.data.ok ? 'Zapisano, ale cache nie został wyczyszczony. Wyczyść cache ręcznie w WordPress.' : `Zapisano, ale cache WP nie działa: ${diag.data.error}`);
          } catch {
            showError(msg ? `Zapisano, ale czyszczenie cache nie powiodło się: ${msg}` : 'Zapisano ustawienia, ale nie udało się wyczyścić cache WordPress.');
          }
        }
      }
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const themeBannerRadius = settings?.settings?.borderStyle === 'square' ? 0 : settings?.settings?.borderRadius ?? 8;
  const previewBannerRadius = form.pages.bannerRadiusMode === 'custom'
    ? Math.min(80, Math.max(0, Number(form.pages.bannerBorderRadius) || 0))
    : themeBannerRadius;

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
          <Monitor className="w-6 h-6 text-accent-primary" />
          Wygląd stron
        </h1>
        <p className="text-text-secondary mt-1">Konfiguracja wyglądu poszczególnych stron sklepu</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-surface-raised border border-border-subtle">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
              activeTab === tab.key
                ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover border border-transparent'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden md:inline">{tab.name}</span>
          </button>
        ))}
      </div>

      {/* ─── General Tab ──────────────────────────────────────────────────── */}
      {activeTab === 'general' && (
        <div className="animate-fade-in">
          <KreatorSettingsSection
            integrationId={integrationId}
            settings={settings}
            onSaved={onSaved}
            showSuccess={() => undefined}
            showError={showError}
            apiAdapter={adapter}
            embedded
          />
        </div>
      )}

      {/* ─── Pages Tab ───────────────────────────────────────────────────── */}
      {activeTab === 'pages' && (
        <div className="space-y-6 animate-fade-in">
          <div className="card">
            <SectionHeader
              icon={MousePointerClick}
              title="Zastosowanie"
              description="Określ, które strony WordPress mają używać globalnego baneru"
              accent="primary"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {([
                {
                  value: 'all' as const,
                  label: 'Zastosuj do wszystkich',
                  description: 'Każda strona typu Strona użyje tego baneru automatycznie.',
                },
                {
                  value: 'selected' as const,
                  label: 'Zastosuj do wybranych',
                  description: 'W edytorze konkretnej strony pojawi się checkbox włączający baner.',
                },
              ]).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updatePages('bannerApplyMode', option.value)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    form.pages.bannerApplyMode === option.value
                      ? 'border-accent-primary/50 bg-accent-primary/10'
                      : 'border-border bg-surface-raised hover:border-border-strong'
                  }`}
                >
                  <div className="text-sm font-medium text-text-primary">{option.label}</div>
                  <div className="text-xs text-text-secondary mt-1">{option.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <SectionHeader
              icon={Layout}
              title="Układ baneru"
              description="Ustaw szerokość sekcji i zaokrąglenie rogów baneru"
              accent="secondary"
            />
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Typ sekcji</label>
                <select
                  value={form.pages.bannerSectionType}
                  onChange={(e) => updatePages('bannerSectionType', e.target.value)}
                  className="input w-full max-w-md"
                >
                  {PAGE_SECTION_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Zaokrąglenie rogów</label>
                  <select
                    value={form.pages.bannerRadiusMode}
                    onChange={(e) => updatePages('bannerRadiusMode', e.target.value)}
                    className="input w-full"
                  >
                    {PAGE_RADIUS_MODES.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-text-muted mt-1">
                    Domyślne używa ustawienia kreatora: {themeBannerRadius}px.
                  </p>
                </div>

                {form.pages.bannerRadiusMode === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Własne zaokrąglenie (px)</label>
                    <input
                      type="number"
                      min={0}
                      max={80}
                      value={form.pages.bannerBorderRadius}
                      onChange={(e) => updatePages('bannerBorderRadius', Math.min(80, Math.max(0, Number(e.target.value) || 0)))}
                      className="input w-full"
                    />
                  </div>
                )}
              </div>

              <div>
                <div className="mb-2">
                  <div className="text-sm font-medium text-text-secondary">Margines od góry</div>
                  <div className="mt-0.5 text-xs text-text-muted">Osobny odstęp baneru od nawigacji na desktopie i mobile.</div>
                </div>
                <div className="space-y-2.5">
                  <Slider
                    icon={Monitor}
                    value={form.pages.bannerTopMargin.desktop}
                    onChange={(desktop) => updatePages('bannerTopMargin', { ...form.pages.bannerTopMargin, desktop })}
                    min={0}
                    max={240}
                    step={1}
                    unit="px"
                  />
                  <Slider
                    icon={Smartphone}
                    value={form.pages.bannerTopMargin.mobile}
                    onChange={(mobile) => updatePages('bannerTopMargin', { ...form.pages.bannerTopMargin, mobile })}
                    min={0}
                    max={240}
                    step={1}
                    unit="px"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <SectionHeader
              icon={ImageIcon}
              title="Tło baneru"
              description="Wybierz kolor tła albo zdjęcie z biblioteki mediów WordPress"
              accent="secondary"
            />
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 max-w-md">
                {([
                  { value: 'color' as const, label: 'Kolor' },
                  { value: 'image' as const, label: 'Zdjęcie' },
                ]).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => updatePages('bannerBackgroundType', option.value)}
                    className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                      form.pages.bannerBackgroundType === option.value
                        ? 'border-accent-primary/50 bg-accent-primary/10 text-accent-primary'
                        : 'border-border bg-surface-raised text-text-secondary hover:border-border-strong'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {form.pages.bannerBackgroundType === 'color' ? (
                <HexColorInput
                  label="Kolor tła"
                  value={form.pages.bannerBackgroundColor}
                  onChange={(value) => updatePages('bannerBackgroundColor', value)}
                  fallback="#f5f7fb"
                />
              ) : (
                <div className="space-y-3">
                  {form.pages.bannerBackgroundImageUrl ? (
                    <div className="flex items-center gap-4 rounded-lg border border-border-subtle bg-surface-raised p-3">
                      <img
                        src={form.pages.bannerBackgroundImageUrl}
                        alt=""
                        className="h-20 w-32 rounded-lg object-cover border border-border"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-text-primary">Zdjęcie baneru</p>
                        <p className="text-xs text-text-muted truncate">{form.pages.bannerBackgroundImageUrl}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowPageBannerMediaPicker(true)}
                        className="btn-secondary text-xs flex items-center gap-1.5"
                        disabled={!adapter.isTenantScoped}
                      >
                        <Upload className="w-3.5 h-3.5" />
                        Zmień
                      </button>
                      <button
                        type="button"
                        onClick={() => updatePages('bannerBackgroundImageUrl', null)}
                        className="p-2 rounded-lg text-text-secondary hover:text-danger hover:bg-danger/10 transition-colors"
                        title="Usuń zdjęcie"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowPageBannerMediaPicker(true)}
                      disabled={!adapter.isTenantScoped}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-accent-primary/25 bg-accent-primary/5 px-4 py-8 text-sm font-medium text-accent-primary transition-colors hover:border-accent-primary/40 hover:bg-accent-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Upload className="w-4 h-4" />
                      Wybierz lub wgraj zdjęcie z biblioteki WordPress
                    </button>
                  )}

                  {!adapter.isTenantScoped && (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Adres zdjęcia</label>
                      <input
                        type="text"
                        value={form.pages.bannerBackgroundImageUrl || ''}
                        onChange={(e) => updatePages('bannerBackgroundImageUrl', e.target.value || null)}
                        placeholder="https://..."
                        className="input w-full"
                      />
                      <p className="text-xs text-text-muted mt-1">
                        W domyślnych ustawieniach globalnych nie ma połączenia z konkretną biblioteką WP.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <SectionHeader
              icon={Type}
              title="Tekst i układ"
              description="Kolor tytułu, wyrównanie, breadcrumbs i wysokość baneru"
              accent="tertiary"
            />
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <HexColorInput
                  label="Kolor tekstu"
                  value={form.pages.bannerTextColor}
                  onChange={(value) => updatePages('bannerTextColor', value)}
                  fallback="#111827"
                />
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Wyrównanie tekstu</label>
                  <select
                    value={form.pages.bannerTextAlign}
                    onChange={(e) => updatePages('bannerTextAlign', e.target.value)}
                    className="input w-full"
                  >
                    <option value="left">Do lewej</option>
                    <option value="center">Do środka</option>
                    <option value="right">Do prawej</option>
                  </select>
                </div>
              </div>

              <BreakpointSlider
                label="Wysokość baneru"
                description="Osobna wysokość dla desktop, tablet i mobile."
                value={form.pages.bannerHeight}
                onChange={(value) => updatePages('bannerHeight', value)}
                min={60}
                max={520}
                step={10}
                unit="px"
              />

              <Toggle
                label="Pokaż breadcrumbs pod tytułem"
                description="Przykład: Strona Główna / Sklep"
                checked={form.pages.showBreadcrumbs}
                onChange={(value) => updatePages('showBreadcrumbs', value)}
              />
            </div>
          </div>

          <div
            className={`border border-border-subtle overflow-hidden ${
              form.pages.bannerSectionType === 'contained' ? 'mx-auto max-w-5xl' : 'w-full'
            }`}
            style={{
              marginTop: form.pages.bannerTopMargin.desktop,
              borderRadius: previewBannerRadius,
              backgroundColor: form.pages.bannerBackgroundColor,
              backgroundImage: form.pages.bannerBackgroundType === 'image' && form.pages.bannerBackgroundImageUrl
                ? `url(${form.pages.bannerBackgroundImageUrl})`
                : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              minHeight: Math.min(form.pages.bannerHeight.desktop, 260),
              color: form.pages.bannerTextColor,
            }}
          >
            <div
              className="flex min-h-[180px] flex-col justify-center px-8 py-8"
              style={{ textAlign: form.pages.bannerTextAlign }}
            >
              <h3 className="text-3xl font-bold">Sklep</h3>
              {form.pages.showBreadcrumbs && (
                <p className="mt-3 text-sm opacity-80">Strona Główna / Sklep</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Shop / Archive Tab ────────────────────────────────────────────── */}
      {activeTab === 'shopArchive' && (
        <div className="space-y-6 animate-fade-in">
          <div className="card">
            <SectionHeader icon={LayoutGrid} title="Siatka produktów" description="Liczba kolumn i produktów na stronie listingu" accent="primary" />
            <div className="space-y-3">
              <SliderCard
                label="Kolumny"
                value={form.shopArchive.columns}
                onChange={(v) => updateShopArchive('columns', v)}
                min={2}
                max={6}
              />
              <SliderCard
                label="Produktów na stronę"
                value={form.shopArchive.productsPerPage}
                onChange={(v) => updateShopArchive('productsPerPage', v)}
                min={4}
                max={48}
                step={4}
              />
            </div>
          </div>

          <div className="card">
            <SectionHeader icon={Layout} title="Styl karty produktu" description="Wybierz układ pojedynczej karty na liście" accent="secondary" />
            <CardSelect
              options={PRODUCT_CARD_STYLES}
              value={form.shopArchive.productCardStyle}
              onChange={(v) => updateShopArchive('productCardStyle', v)}
            />
          </div>

          <div className="card">
            <SectionHeader icon={ImageIcon} title="Proporcje zdjęcia" description="Format zdjęcia produktowego na karcie" accent="tertiary" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {IMAGE_RATIOS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => updateShopArchive('imageAspectRatio', r.value)}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    form.shopArchive.imageAspectRatio === r.value
                      ? 'border-accent-primary/50 bg-accent-primary/10'
                      : 'border-border bg-surface-raised hover:border-border-strong'
                  }`}
                >
                  <div className="text-sm text-text-primary">{r.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <SectionHeader icon={Sparkles} title="Efekt hover" description="Animacja zdjęcia po najechaniu kursorem" accent="primary" />
            <div className="grid grid-cols-3 gap-3">
              {HOVER_EFFECTS.map((h) => (
                <button
                  key={h.value}
                  onClick={() => updateShopArchive('hoverEffect', h.value)}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    form.shopArchive.hoverEffect === h.value
                      ? 'border-accent-primary/50 bg-accent-primary/10'
                      : 'border-border bg-surface-raised hover:border-border-strong'
                  }`}
                >
                  <div className="text-sm text-text-primary">{h.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <SectionHeader icon={Eye} title="Widoczność elementów" description="Co pokazać na liście produktów i kartach" accent="secondary" />
            <div className="space-y-3">
              <Toggle
                label="Sortowanie"
                description="Pokaż dropdown sortowania produktów"
                checked={form.shopArchive.showSorting}
                onChange={(v) => updateShopArchive('showSorting', v)}
              />
              <Toggle
                label="Liczba wyników"
                description="Pokaż 'Wyświetlanie X–Y z Z wyników'"
                checked={form.shopArchive.showResultCount}
                onChange={(v) => updateShopArchive('showResultCount', v)}
              />
              <Toggle
                label="Sidebar z filtrami"
                description="Boczny panel z kategoriami i filtrami cenowymi"
                checked={form.shopArchive.showSidebar}
                onChange={(v) => updateShopArchive('showSidebar', v)}
              />
              <Toggle
                label='Przycisk "Dodaj do koszyka"'
                description="Pokaż przycisk na karcie produktu"
                checked={form.shopArchive.showAddToCartButton}
                onChange={(v) => updateShopArchive('showAddToCartButton', v)}
              />
              <Toggle
                label="Ocena (gwiazdki)"
                description="Pokaż ocenę produktu na karcie"
                checked={form.shopArchive.showRating}
                onChange={(v) => updateShopArchive('showRating', v)}
              />
              <Toggle
                label="Badge wyprzedaży"
                description="Pokaż znacznik 'Wyprzedaż' na produktach z obniżoną ceną"
                checked={form.shopArchive.showSaleBadge}
                onChange={(v) => updateShopArchive('showSaleBadge', v)}
              />
            </div>
          </div>
        </div>
      )}

      {/* ─── Product Page Tab ──────────────────────────────────────────────── */}
      {activeTab === 'productPage' && (
        <div className="space-y-6 animate-fade-in">
          {form.productPage.galleryType !== 'full-width' && (
            <div className="card">
              <SectionHeader icon={Columns2} title="Ustawienia kolumn" description="Proporcje i odstępy między galerią a opisem" accent="primary" />
              <div className="space-y-3">
              <SliderCard
                label="Proporcja"
                value={form.productPage.columnRatio}
                onChange={(v) => updateProduct('columnRatio', v)}
                min={30}
                max={70}
                step={5}
                formatValue={(v) => `Lewa: ${v}% / Prawa: ${100 - v}%`}
                description="Stosunek szerokości lewej i prawej kolumny na stronie produktu (desktop)"
              />
              <Toggle
                label="Własny odstęp galeria ↔ krótki opis"
                description="Ustaw inny odstęp między galerią a krótkim opisem produktu niż globalny (z ustawień ogólnych)"
                checked={form.productPage.productPageColumnGap !== null}
                onChange={(v) => updateProduct('productPageColumnGap', v ? (settings?.settings?.columnGap ?? 32) : null)}
              />
              {form.productPage.productPageColumnGap !== null && (
                <SliderCard
                  label="Odstęp galeria ↔ krótki opis"
                  value={form.productPage.productPageColumnGap}
                  onChange={(v) => updateProduct('productPageColumnGap', v)}
                  min={0}
                  max={100}
                  step={1}
                  unit="px"
                />
              )}
              </div>
            </div>
          )}

          <div className="card">
            <SectionHeader icon={Image} title="Galeria produktowa" description="Wybierz układ galerii zdjęć produktu" accent="tertiary" />
            <GalleryTypePicker
              value={form.productPage.galleryType}
              onChange={(v) => updateProduct('galleryType', v)}
            />
          </div>

          {/* ─── Price Styling Section ────────────────────────────────────────── */}
          <div className="card">
            <SectionHeader icon={Tag} title="Cena" description="Styl wyświetlania ceny i plakietki rabatowe" accent="warning" />
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-border-subtle bg-surface-raised px-3 py-2 text-xs text-text-secondary">
              <Type className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-accent-primary" />
              <span>
                Wielkości i fonty ceny regularnej, promocyjnej oraz plakietek ustawisz w{' '}
                {typographyHref ? (
                  <a href={typographyHref} className="font-medium text-accent-primary hover:underline">
                    Typografia - Elementy sklepu
                  </a>
                ) : (
                  <span className="font-medium text-text-primary">Typografia - Elementy sklepu</span>
                )}
                .
              </span>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Styl wyświetlania ceny</label>
                <div className="grid grid-cols-2 gap-3 max-w-md">
                  {([
                    { value: 'default' as const, label: 'Domyślny', desc: 'Standardowy styl WooCommerce' },
                    { value: 'enhanced' as const, label: 'Rozszerzony', desc: 'Cena + plakietki rabatowe i oszczędności' },
                  ]).map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => updateProduct('priceStyle', opt.value)}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        form.productPage.priceStyle === opt.value
                          ? 'border-accent-primary/50 bg-accent-primary/10'
                          : 'border-border bg-surface-raised hover:border-border-strong'
                      }`}
                    >
                      <div className="text-sm font-medium text-text-primary">{opt.label}</div>
                      <div className="text-xs text-text-secondary mt-1">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {form.productPage.priceStyle === 'enhanced' && (
                <div className="space-y-4 p-4 rounded-lg border border-border-subtle bg-surface-raised">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Kolor starej ceny (przekreślonej)</label>
                      <BadgeColorPicker
                        value={form.productPage.priceOldColor}
                        onChange={(v) => updateProduct('priceOldColor', v)}
                        colors={paletteColors}
                        defaultLabel="Domyślny"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Kolor nowej ceny (promocyjnej)</label>
                      <BadgeColorPicker
                        value={form.productPage.priceNewColor}
                        onChange={(v) => updateProduct('priceNewColor', v)}
                        colors={paletteColors}
                        defaultLabel="Domyślny"
                      />
                    </div>
                  </div>

                  <Toggle
                    label="Badge rabatowy (-X%)"
                    description="Plakietka z procentem zniżki obok ceny"
                    checked={form.productPage.showDiscountBadge}
                    onChange={(v) => updateProduct('showDiscountBadge', v)}
                  />
                  {form.productPage.showDiscountBadge && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-4 border-l-2 border-border-subtle">
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">Tło badge'a rabatowego</label>
                        <BadgeColorPicker
                          value={form.productPage.discountBadgeBgColor}
                          onChange={(v) => updateProduct('discountBadgeBgColor', v)}
                          colors={paletteColors}
                          defaultLabel="Domyślny"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">Tekst badge'a rabatowego</label>
                        <BadgeColorPicker
                          value={form.productPage.discountBadgeTextColor}
                          onChange={(v) => updateProduct('discountBadgeTextColor', v)}
                          colors={paletteColors}
                          defaultLabel="Domyślny"
                        />
                      </div>
                    </div>
                  )}

                  <Toggle
                    label="Badge oszczędności"
                    description="Plakietka 'Oszczędzasz: X zł' obok ceny"
                    checked={form.productPage.showSavingsBadge}
                    onChange={(v) => updateProduct('showSavingsBadge', v)}
                  />
                  {form.productPage.showSavingsBadge && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-4 border-l-2 border-border-subtle">
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">Tło badge'a oszczędności</label>
                        <BadgeColorPicker
                          value={form.productPage.savingsBadgeBgColor}
                          onChange={(v) => updateProduct('savingsBadgeBgColor', v)}
                          colors={paletteColors}
                          defaultLabel="Domyślny"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">Tekst badge'a oszczędności</label>
                        <BadgeColorPicker
                          value={form.productPage.savingsBadgeTextColor}
                          onChange={(v) => updateProduct('savingsBadgeTextColor', v)}
                          colors={paletteColors}
                          defaultLabel="Domyślny"
                        />
                      </div>
                    </div>
                  )}

                  <Toggle
                    label="Separator cenowy"
                    description="Pozioma kreska nad i pod modułem cenowym"
                    checked={form.productPage.priceSeparator}
                    onChange={(v) => updateProduct('priceSeparator', v)}
                  />
                </div>
              )}
            </div>
            <div className="mt-5 pt-5 border-t border-border-subtle">
              <Toggle
                label="Cena Omnibus"
                description="Najniższa cena z 30 dni przed obniżką (Dyrektywa Omnibus)"
                checked={form.productPage.showOmnibusPrice}
                onChange={(v) => updateProduct('showOmnibusPrice', v)}
              />
            </div>
          </div>

          <div className="card">
            <SectionHeader icon={Eye} title="Widoczność elementów" description="Co pokazać na stronie produktu" accent="secondary" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Toggle
                label="Pokaż cross-sell'e"
                description="Wyświetlaj sugestie powiązanych produktów (cross-sell) pod opisem"
                checked={form.productPage.showRelatedProducts}
                onChange={(v) => updateProduct('showRelatedProducts', v)}
              />
              {form.productPage.showRelatedProducts && (
                <div className="md:col-span-2">
                  <SliderCard
                    label="Liczba powiązanych produktów"
                    value={form.productPage.relatedProductsCount}
                    onChange={(v) => updateProduct('relatedProductsCount', v)}
                    min={2}
                    max={8}
                  />
                </div>
              )}
              <Toggle
                label="Pokaż up-sell'e"
                description="Wyświetlaj produkty o wyższej wartości (up-sell)"
                checked={form.productPage.showUpsells}
                onChange={(v) => updateProduct('showUpsells', v)}
              />
              <Toggle
                label="Recenzje"
                description="Sekcja opinii klientów"
                checked={form.productPage.showReviews}
                onChange={(v) => updateProduct('showReviews', v)}
              />
              <Toggle
                label="Zakładki produktu"
                description="Opis, dodatkowe informacje, recenzje w zakładkach"
                checked={form.productPage.showTabs}
                onChange={(v) => updateProduct('showTabs', v)}
              />
              {form.productPage.showTabs && (
                <div className="md:col-span-2 p-4 rounded-lg border border-border-subtle bg-surface-raised">
                  <label className="block text-sm font-medium text-text-primary mb-2">Styl zakładek</label>
                  <select
                    value={form.productPage.tabsStyle}
                    onChange={(e) => updateProduct('tabsStyle', e.target.value)}
                    className="input w-full"
                  >
                    {TABS_STYLES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <Toggle
                label="SKU produktu"
                description="Wyświetlaj numer katalogowy produktu (SKU)"
                checked={form.productPage.showSku}
                onChange={(v) => updateProduct('showSku', v)}
              />
              <Toggle
                label="Kategorie"
                description="Wyświetlaj kategorie produktu"
                checked={form.productPage.showCategories}
                onChange={(v) => updateProduct('showCategories', v)}
              />
              <Toggle
                label="Sticky Call to Action"
                description="Przycisk przyklejony na dole ekranu przy przewijaniu"
                checked={form.productPage.stickyAddToCart}
                onChange={(v) => updateProduct('stickyAddToCart', v)}
              />
              <label className="block text-sm text-text-secondary">Działanie dolnego paska
                <Select value={form.productPage.stickyCartMode} onChange={(e) => updateProduct('stickyCartMode', e.target.value)}>
                  <option value="direct">Dodawanie do koszyka</option>
                  <option value="package-picker">Wybór pakietu w panelu</option>
                </Select>
              </label>
              <Toggle
                label="Badge wyprzedaży"
                description="Pokaż badge 'Promocja!' na produktach z obniżoną ceną"
                checked={form.productPage.showSaleBadge}
                onChange={(v) => updateProduct('showSaleBadge', v)}
              />
              {(form.productPage.galleryType === 'classic' || form.productPage.galleryType === 'vertical-scroll') && (
                <Toggle
                  label="Lightbox galerii"
                  description="Powiększ zdjęcie po kliknięciu (tylko galerie EcomFlow)"
                  checked={form.productPage.galleryLightbox}
                  onChange={(v) => updateProduct('galleryLightbox', v)}
                />
              )}
            </div>
          </div>

          <div className="card">
            <SectionHeader icon={MousePointerClick} title="Przycisk Call to Action" description="Styl, kolor i animacja przycisku 'Dodaj do koszyka'" accent="primary" />
            <div className="space-y-3">
              {/* Style: Wypełniony / Obrys */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Styl przycisku</label>
                <div className="grid grid-cols-2 gap-3 max-w-xs">
                  {BUTTON_STYLES.map((bs) => (
                    <button
                      key={bs.value}
                      onClick={() => updateProduct('addToCartButtonStyle', bs.value)}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        form.productPage.addToCartButtonStyle === bs.value
                          ? 'border-accent-primary/50 bg-accent-primary/10'
                          : 'border-border bg-surface-raised hover:border-border-strong'
                      }`}
                    >
                      <Square className={`w-4 h-4 mx-auto mb-1 ${
                        bs.value === 'outline' ? 'text-accent-primary' : ''
                      }`} />
                      <div className="text-xs text-text-primary">{bs.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Color from palette */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Kolor przycisku</label>
                <PaletteColorPicker
                  value={form.productPage.addToCartButtonColor}
                  onChange={(v) => updateProduct('addToCartButtonColor', v)}
                  colors={paletteColors}
                />
              </div>

              {/* Quantity + CTA toggle */}
              <Toggle
                label="Pole ilości"
                description="Pokaż selektor ilości obok przycisku 'Dodaj do koszyka'"
                checked={form.productPage.addToCartShowQuantity}
                onChange={(v) => updateProduct('addToCartShowQuantity', v)}
              />

              {/* Pulse animation */}
              <Toggle
                label="Animacja pulsowania"
                description="Animacja pulsowania przycisku przyciągająca uwagę klienta"
                checked={form.productPage.addToCartPulseEnabled}
                onChange={(v) => updateProduct('addToCartPulseEnabled', v)}
              />
              {form.productPage.addToCartPulseEnabled && (
                <div className="p-4 rounded-lg border border-border-subtle bg-surface-raised space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Kolor pulsowania</label>
                    <PaletteColorPicker
                      value={form.productPage.addToCartPulseColor}
                      onChange={(v) => updateProduct('addToCartPulseColor', v)}
                      colors={paletteColors}
                    />
                  </div>
                  <SliderCard
                    label="Przezroczystość"
                    value={Math.round(form.productPage.addToCartPulseOpacity * 100)}
                    onChange={(v) => updateProduct('addToCartPulseOpacity', v / 100)}
                    min={10}
                    max={100}
                    step={5}
                    unit="%"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Cart Tab ──────────────────────────────────────────────────────── */}
      {activeTab === 'cart' && (
        <div className="space-y-6 animate-fade-in">
          <div className="card">
            <SectionHeader icon={Layout} title="Układ koszyka" description="Wybierz układ wizualny koszyka" accent="primary" />
            <CardSelect
              options={CART_LAYOUTS}
              value={form.cart.layout}
              onChange={(v) => updateCart('layout', v)}
            />
          </div>

          <div className="card">
            <SectionHeader icon={ListChecks} title="Elementy koszyka" description={form.cart.layout === 'compact' ? 'Skonfiguruj interaktywny koszyk wysuwany z prawej strony' : 'Co pokazać na stronie koszyka'} accent="secondary" />
            <div className="space-y-3">
              <Toggle
                label="Komunikaty WooCommerce"
                description="Pokazuj komunikaty typu dodano do koszyka, usunięto produkt, cofnięcie akcji i informacje kuponów na stronie produktu, koszyku i kasie"
                checked={form.cart.showWooCommerceNotices ?? true}
                onChange={(v) => updateCart('showWooCommerceNotices', v)}
              />
              {form.cart.layout === 'compact' && <Toggle label="Licznik w koszyku" description="Widoczność wspólnego paska w wysuwanym koszyku" checked={form.cart.reservationEnabled} onChange={(v) => updateCart('reservationEnabled', v)} />}
              {(form.cart.reservationEnabled || form.checkout.showCheckoutTimer) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg border border-border-subtle bg-surface-raised">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">Czas wspólnego licznika (min)</label>
                        <input type="number" min={1} max={180} value={form.cart.reservationDurationMinutes} onChange={(e) => updateCart('reservationDurationMinutes', Number(e.target.value))} className="input w-full" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">Przerwa po wygaśnięciu przed resetem (min)</label>
                        <input type="number" min={1} max={1440} value={form.cart.reservationResetMinutes} onChange={(e) => updateCart('reservationResetMinutes', Number(e.target.value))} className="input w-full" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-text-primary mb-2">Komunikat aktywny</label>
                        <input type="text" value={form.cart.reservationMessage} onChange={(e) => updateCart('reservationMessage', e.target.value)} className="input w-full" />
                        <p className="text-xs text-text-muted mt-1">Użyj {'{time}'}, aby wstawić aktualny czas.</p>
                      </div>
                      <div className="md:col-span-2">
                        <Toggle label="Ukryj pasek po zakończeniu czasu" checked={form.cart.reservationHideExpired} onChange={(v) => updateCart('reservationHideExpired', v)} />
                        <label className="block text-sm font-medium text-text-primary mb-2 mt-4">Komunikat po zakończeniu czasu</label>
                        <input type="text" value={form.cart.reservationExpiredMessage} onChange={(e) => updateCart('reservationExpiredMessage', e.target.value)} className="input w-full" />
                      </div>
                    </div>
                  )}

              {form.cart.layout === 'standard' ? (
                <>
                  <Toggle label="Pole kuponu" description="Pokaż pole na kod rabatowy" checked={form.cart.showCouponField} onChange={(v) => updateCart('showCouponField', v)} />
                  <Toggle label="Nagłówek koszyka" description={'Pokaż tytuł Koszyk oraz link "Kontynuuj zakupy" nad produktami w koszyku'} checked={form.cart.showCartHeader ?? true} onChange={(v) => updateCart('showCartHeader', v)} />
                  <Toggle label="Cross-sells" description="Produkty uzupełniające wyświetlane w koszyku" checked={form.cart.showCrossSells} onChange={(v) => updateCart('showCrossSells', v)} />
                  {form.cart.showCrossSells && <SliderCard label="Liczba cross-selli" value={form.cart.crossSellsCount} onChange={(v) => updateCart('crossSellsCount', v)} min={2} max={6} />}
                  <Toggle label="Kontynuuj zakupy" description="Przycisk powrotu do sklepu" checked={form.cart.showContinueShopping} onChange={(v) => updateCart('showContinueShopping', v)} />
                  {form.cart.showContinueShopping && (
                    <div className="p-4 rounded-lg border border-border-subtle bg-surface-raised">
                      <label className="block text-sm font-medium text-text-primary mb-2">URL powrotu do zakupów</label>
                      <input type="text" value={form.cart.continueShoppingUrl} onChange={(e) => updateCart('continueShoppingUrl', e.target.value)} placeholder="Domyślny (strona sklepu)" className="input w-full" />
                    </div>
                  )}
                  <Toggle label="Szacunkowa dostawa" description="Pokaż szacowany czas dostawy" checked={form.cart.showEstimatedDelivery} onChange={(v) => updateCart('showEstimatedDelivery', v)} />
                  <Toggle label="Pasek darmowej dostawy" description="Pokaż ile brakuje do darmowej dostawy" checked={form.cart.showFreeShippingBar} onChange={(v) => updateCart('showFreeShippingBar', v)} />
                  {form.cart.showFreeShippingBar && (
                    <div className="p-4 rounded-lg border border-border-subtle bg-surface-raised">
                      <label className="block text-sm font-medium text-text-primary mb-2">Próg darmowej dostawy (PLN)</label>
                      <input type="number" value={form.cart.freeShippingThreshold ?? ''} onChange={(e) => updateCart('freeShippingThreshold', e.target.value ? Number(e.target.value) : null)} placeholder="np. 200" min={0} className="input w-48" />
                    </div>
                  )}
                  <Toggle label="Automatyczna aktualizacja koszyka" description="Po zmianie ilości produktu koszyk sam się przelicza, bez klikania „Zaktualizuj koszyk”" checked={form.cart.autoUpdate ?? false} onChange={(v) => updateCart('autoUpdate', v)} />
                </>
              ) : (
                <>
                  <Toggle label="Pasek darmowej dostawy" description="Kolor postępu pobiera główny kolor szablonu" checked={form.cart.compactFreeShippingEnabled} onChange={(v) => updateCart('compactFreeShippingEnabled', v)} />
                  {form.cart.compactFreeShippingEnabled && (
                    <div className="p-4 rounded-lg border border-border-subtle bg-surface-raised">
                      <label className="block text-sm font-medium text-text-primary mb-2">Próg darmowej dostawy (PLN)</label>
                      <input type="number" value={form.cart.freeShippingThreshold ?? ''} onChange={(e) => updateCart('freeShippingThreshold', e.target.value ? Number(e.target.value) : null)} placeholder="np. 200" min={0} className="input w-48" />
                    </div>
                  )}

                  <Toggle label="Cross-sell w koszyku" description="Produkty można dodać bez opuszczania wysuwanego koszyka" checked={form.cart.compactCrossSellsEnabled} onChange={(v) => updateCart('compactCrossSellsEnabled', v)} />
                  {form.cart.compactCrossSellsEnabled && (
                    <div className="p-4 rounded-lg border border-border-subtle bg-surface-raised space-y-4">
                      <label className="block text-sm font-medium text-text-primary mb-2">Produkty cross-sell</label>
                      <WcMultiProductPicker integrationId={integrationId} value={form.cart.compactCrossSellProductIds} onChange={(ids) => updateCart('compactCrossSellProductIds', ids)} />

                      <div className="pt-4 border-t border-border-subtle space-y-4">
                        <Toggle
                          label="Plakietka promocyjna"
                          description={'Komunikat wyświetlany pod nagłówkiem "Dobierz do zamówienia"'}
                          checked={form.cart.compactCrossSellBadge.enabled}
                          onChange={(value) => updateCrossSellBadge('enabled', value)}
                        />
                        {form.cart.compactCrossSellBadge.enabled && (
                          <div className="space-y-4">
                            <div>
                              <label htmlFor="compact-cross-sell-badge-text" className="block text-sm font-medium text-text-primary mb-2">Treść plakietki</label>
                              <input
                                id="compact-cross-sell-badge-text"
                                type="text"
                                maxLength={160}
                                value={form.cart.compactCrossSellBadge.text}
                                onChange={(event) => updateCrossSellBadge('text', event.target.value)}
                                className="input w-full"
                              />
                            </div>

                            <div className="rounded-lg border border-border-subtle bg-surface-hover/50 p-3">
                              <p className="text-xs text-text-muted mb-2">Podgląd</p>
                              <span
                                className="inline-flex px-3 py-1.5 text-xs font-semibold"
                                style={{
                                  backgroundColor: form.cart.compactCrossSellBadge.backgroundColor || paletteColors.primary,
                                  color: form.cart.compactCrossSellBadge.textColor || '#ffffff',
                                  borderColor: form.cart.compactCrossSellBadge.borderColor || paletteColors.primary,
                                  borderStyle: form.cart.compactCrossSellBadge.borderStyle,
                                  borderWidth: form.cart.compactCrossSellBadge.borderStyle === 'none' ? 0 : form.cart.compactCrossSellBadge.borderWidth,
                                  borderRadius: form.cart.compactCrossSellBadge.borderRadius,
                                }}
                              >
                                {form.cart.compactCrossSellBadge.text || 'Treść plakietki'}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <HexColorInput
                                label="Kolor tła"
                                value={form.cart.compactCrossSellBadge.backgroundColor || paletteColors.primary}
                                onChange={(value) => updateCrossSellBadge('backgroundColor', value)}
                                fallback={paletteColors.primary}
                              />
                              <HexColorInput
                                label="Kolor fontu"
                                value={form.cart.compactCrossSellBadge.textColor || '#ffffff'}
                                onChange={(value) => updateCrossSellBadge('textColor', value)}
                                fallback="#ffffff"
                              />
                              <HexColorInput
                                label="Kolor ramki"
                                value={form.cart.compactCrossSellBadge.borderColor || paletteColors.primary}
                                onChange={(value) => updateCrossSellBadge('borderColor', value)}
                                fallback={paletteColors.primary}
                              />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-text-primary mb-2">Styl ramki</label>
                                <Select value={form.cart.compactCrossSellBadge.borderStyle} onChange={(event) => updateCrossSellBadge('borderStyle', event.target.value)}>
                                  {CROSS_SELL_BADGE_BORDER_STYLES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </Select>
                              </div>
                              <SliderCard
                                label="Grubość ramki"
                                value={form.cart.compactCrossSellBadge.borderWidth}
                                onChange={(value) => updateCrossSellBadge('borderWidth', value)}
                                min={0}
                                max={8}
                                unit="px"
                              />
                              <SliderCard
                                label="Zaokrąglenie"
                                value={form.cart.compactCrossSellBadge.borderRadius}
                                onChange={(value) => updateCrossSellBadge('borderRadius', value)}
                                min={0}
                                max={40}
                                unit="px"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <Toggle label="Podsumowanie kosztów" description="Produkty, dostawa, oszczędności i łączna kwota" checked={form.cart.compactCostSummaryEnabled} onChange={(v) => updateCart('compactCostSummaryEnabled', v)} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg border border-border-subtle bg-surface-raised">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">Tekst przycisku CTA</label>
                      <input type="text" value={form.cart.compactCtaText} onChange={(e) => updateCart('compactCtaText', e.target.value)} className="input w-full" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">Ikona CTA</label>
                      <Select value={form.cart.compactCtaIcon} onChange={(e) => updateCart('compactCtaIcon', e.target.value)}>
                        {CART_CTA_ICONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </Select>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg border border-border-subtle bg-surface-raised space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-medium text-text-primary">Nagłówki z ikonami</h4>
                        <p className="text-xs text-text-muted">Do 5 argumentów widocznych pod CTA.</p>
                      </div>
                      <button type="button" onClick={addCartBenefit} disabled={form.cart.compactBenefits.length >= 5} className="btn btn-secondary text-xs disabled:opacity-50">
                        <Plus className="w-4 h-4" /> Dodaj
                      </button>
                    </div>
                    {form.cart.compactBenefits.map((benefit, index) => (
                      <div key={index} className="grid grid-cols-[140px_1fr_auto] gap-2 items-center">
                        <Select value={benefit.icon} onChange={(e) => updateCartBenefit(index, 'icon', e.target.value)}>
                          {CART_BENEFIT_ICONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </Select>
                        <input type="text" value={benefit.text} onChange={(e) => updateCartBenefit(index, 'text', e.target.value)} className="input w-full" />
                        <button type="button" onClick={() => removeCartBenefit(index)} aria-label={`Usuń nagłówek ${index + 1}`} className="p-2 text-text-muted hover:text-danger"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Checkout Tab ──────────────────────────────────────────────────── */}
      {activeTab === 'checkout' && (
        <div className="space-y-6 animate-fade-in">
          <div className="card">
            <SectionHeader icon={Layout} title="Układ kasy" description="Wybierz wzorzec procesu kasowego" accent="primary" />
            <CardSelect
              options={CHECKOUT_LAYOUTS}
              value={form.checkout.layout}
              onChange={(v) => updateCheckout('layout', v)}
            />
          </div>

          <div className="card space-y-4">
            <SectionHeader icon={Layout} title="Podsumowanie i korzyści" description="Opcje nowego układu z dostawą przed danymi" accent="primary" />
            <Toggle label="Pokaż aktywną promocję" checked={form.checkout.showPromotion} onChange={(v) => updateCheckout('showPromotion', v)} />
            <label className="block text-sm text-text-secondary">Etykieta promocji<input className="input w-full mt-2" maxLength={240} value={form.checkout.promotionLabel} onChange={(e) => updateCheckout('promotionLabel', e.target.value)} /></label>
            <Toggle label="Pokaż licznik koszyka w kasie" description="Ten sam pasek, czas i stan co w koszyku. Wspólne ustawienia: Koszyk." checked={form.checkout.showCheckoutTimer} onChange={(v) => updateCheckout('showCheckoutTimer', v)} />
            <label className="block text-sm text-text-secondary">Komunikat zwrotów<input className="input w-full mt-2" maxLength={240} value={form.checkout.returnsMessage} onChange={(e) => updateCheckout('returnsMessage', e.target.value)} /></label>
            <Toggle label="Pokaż mini sekcję korzyści" checked={form.checkout.showTrustBadges} onChange={(v) => updateCheckout('showTrustBadges', v)} />
            {form.checkout.showTrustBadges && <div className="space-y-3">
              {form.checkout.trustBadges.map((badge, index) => <div key={badge.id} className="grid gap-3 sm:grid-cols-[180px_1fr_auto] items-end">
                <div className="min-w-0"><label htmlFor={`trust-icon-${badge.id}`} className="block text-sm text-text-secondary mb-2">Ikona {index + 1}</label><IconPickerField id={`trust-icon-${badge.id}`} variant="form" value={badge.icon} onChange={(icon) => updateCheckout('trustBadges', form.checkout.trustBadges.map((item, i) => i === index ? { ...item, icon } : item))} /></div>
                <label className="block text-sm text-text-secondary">Tekst {index + 1}<input className="input w-full mt-2" maxLength={120} value={badge.text} onChange={(e) => updateCheckout('trustBadges', form.checkout.trustBadges.map((item, i) => i === index ? { ...item, text: e.target.value } : item))} placeholder="Puste pole ukrywa korzyść" /></label>
                <Button variant="icon" aria-label={`Usuń korzyść ${index + 1}`} leftIcon={<Trash2 />} onClick={() => updateCheckout('trustBadges', form.checkout.trustBadges.filter((_, i) => i !== index))} />
              </div>)}
              <Button variant="ghost" size="sm" leftIcon={<Plus />} disabled={form.checkout.trustBadges.length >= 8} onClick={() => updateCheckout('trustBadges', [...form.checkout.trustBadges, { id: crypto.randomUUID(), icon: 'Check', text: '' }])}>Dodaj korzyść</Button>
            </div>}

            <Toggle label="Uprość adres dla odbioru w punkcie" description="Wymaga oznaczenia metody jako Odbiór w punkcie w Sklep → Wysyłka. Obecnie działa przy pobraniu; płatności online i prośba o fakturę zachowują adres." checked={form.checkout.simplifyPickupAddress} onChange={(v) => updateCheckout('simplifyPickupAddress', v)} />
            <p className="text-xs text-text-muted">Czas licznika pochodzi z ustawień koszyka. Komunikat rezerwacji wymaga rzeczywistej rezerwacji zapasu. Uproszczenie adresu wymaga zgodności bramki płatniczej i danych do faktury.</p>
          </div>

          {adapter.isTenantScoped && <div className="card space-y-3">
            <label className="block text-sm text-text-secondary">Obsługa map punktów odbioru
              <Select value={form.checkout.pickupMapMode} onChange={e => updateCheckout('pickupMapMode', e.target.value as 'plugins' | 'ecomflow')}>
                <option value="plugins">Mapy z wtyczek</option><option value="ecomflow">Mapy EcomFlow</option>
              </Select>
            </label>
            <p className="text-xs text-text-muted">Ustawienie obejmuje cały sklep i lejki. Skonfiguruj InPost Geowidget w Integracje → Mapy punktów odbioru, następnie przypisz go do metod w Sklep → Wysyłka. Nieprzypisane i nieobsługiwane metody nadal używają map wtyczek.</p>
          </div>}

          {form.checkout.layout === 'multi-step' && (
            <div className="card">
              <SectionHeader icon={Type} title="Nazwy kroków" description="Tekst etykiet poszczególnych kroków" accent="tertiary" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Krok 1: Dostawa</label>
                  <input
                    type="text"
                    value={form.checkout.stepLabels.shipping}
                    onChange={(e) => updateCheckoutLabels('shipping', e.target.value)}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Krok 2: Płatność</label>
                  <input
                    type="text"
                    value={form.checkout.stepLabels.payment}
                    onChange={(e) => updateCheckoutLabels('payment', e.target.value)}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Krok 3: Podsumowanie</label>
                  <input
                    type="text"
                    value={form.checkout.stepLabels.review}
                    onChange={(e) => updateCheckoutLabels('review', e.target.value)}
                    className="input w-full"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <SectionHeader icon={FormInput} title="Pola formularza" description="Globalna kolejność, szerokość i widoczność pól. Konfiguracja w konkretnym lejku ma pierwszeństwo." accent="secondary" />
            <div className="space-y-3">
              <CheckoutFieldsEditor
                fields={form.checkout.checkoutFields}
                onChange={(fields) => updateCheckout('checkoutFields', fields)}
              />
              <Toggle
                label="Notatki do zamówienia"
                description="Pole na uwagi klienta do zamówienia"
                checked={form.checkout.showOrderNotes}
                onChange={(v) => updateCheckout('showOrderNotes', v)}
              />
              <Toggle
                label="Dane firmowe"
                description="Pola na nazwę firmy i NIP"
                checked={form.checkout.showCompanyFields}
                onChange={(v) => updateCheckout('showCompanyFields', v)}
              />
              <Toggle
                label="Numer telefonu"
                description="Wymagane dla logistyki i nadawania przesyłek"
                checked={true}
                onChange={() => updateCheckout('showPhoneField', true)}
                disabled
              />
              <Toggle
                label="Pole kuponu"
                description="Pole na kod rabatowy w kasie"
                checked={form.checkout.showCouponField}
                onChange={(v) => updateCheckout('showCouponField', v)}
              />
              <div className="space-y-4">
                <Toggle
                  label="Akceptacja regulaminu przyciskiem zamówienia"
                  description="Włącz: komunikat pod CTA zamiast checkboxa. Wyłącz: standardowy checkbox WooCommerce."
                  checked={form.checkout.termsAcceptance.mode === 'cta'}
                  onChange={(v) => updateCheckout('termsAcceptance', { ...form.checkout.termsAcceptance, mode: v ? 'cta' : 'checkbox' })}
                />
                {form.checkout.termsAcceptance.mode === 'cta' && (
                  <div className="space-y-4">
                    <label className="block text-sm text-text-secondary">Komunikat pod przyciskiem
                      <textarea className="input mt-2 w-full" rows={3} maxLength={2000} value={form.checkout.termsAcceptance.notice} onChange={(e) => updateCheckout('termsAcceptance', { ...form.checkout.termsAcceptance, notice: e.target.value })} />
                    </label>
                    <p className="text-xs text-text-muted">{'{terms} wstawia link do regulaminu (wymagany), {privacy} link do polityki prywatności. Puste adresy korzystają ze stron ustawionych w WordPressie. Bez strony regulaminu kasa zachowa checkbox. Pozostałe zgody pozostają niezależne.'}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {([
                        ['termsLabel', 'Tekst linku do regulaminu'], ['termsUrl', 'Adres regulaminu'],
                        ['privacyLabel', 'Tekst linku do polityki prywatności'], ['privacyUrl', 'Adres polityki prywatności'],
                      ] as const).map(([key, label]) => (
                        <label key={key} className="block text-sm text-text-secondary">{label}
                          <input className="input mt-2 w-full" value={form.checkout.termsAcceptance[key]} onChange={(e) => updateCheckout('termsAcceptance', { ...form.checkout.termsAcceptance, [key]: e.target.value })} />
                        </label>
                      ))}
                    </div>
                    <Slider label="Rozmiar tekstu" value={form.checkout.termsAcceptance.fontSize} min={12} max={18} unit="px" onChange={(fontSize) => updateCheckout('termsAcceptance', { ...form.checkout.termsAcceptance, fontSize })} />
                    <Select aria-label="Wyrównanie komunikatu" value={form.checkout.termsAcceptance.alignment} onChange={(e) => updateCheckout('termsAcceptance', { ...form.checkout.termsAcceptance, alignment: e.target.value as 'left' | 'center' })}>
                      <option value="left">Wyrównanie do lewej</option>
                      <option value="center">Wyśrodkowanie</option>
                    </Select>
                    <Toggle label="Dodatkowe informacje o sprzedawcy" checked={form.checkout.termsAcceptance.showCompanyInfo} onChange={(showCompanyInfo) => updateCheckout('termsAcceptance', { ...form.checkout.termsAcceptance, showCompanyInfo })} />
                    {form.checkout.termsAcceptance.showCompanyInfo && <label className="block text-sm text-text-secondary">Dane sprzedawcy i dodatkowe informacje
                      <textarea className="input mt-2 w-full" rows={3} maxLength={2000} value={form.checkout.termsAcceptance.companyInfo} onChange={(e) => updateCheckout('termsAcceptance', { ...form.checkout.termsAcceptance, companyInfo: e.target.value })} />
                    </label>}
                  </div>
                )}
              </div>
              <Toggle
                label="Express checkout"
                description="Przyciski szybkiej płatności (Apple Pay, Google Pay)"
                checked={form.checkout.expressCheckoutEnabled}
                onChange={(v) => updateCheckout('expressCheckoutEnabled', v)}
              />
            </div>
          </div>
        </div>
      )}

      {/* ─── Thank You Tab ─────────────────────────────────────────────────── */}
      {activeTab === 'thankYouPage' && (
        <div className="space-y-6 animate-fade-in">
          <div className="card">
            <SectionHeader icon={MessageSquare} title="Treść strony" description="Nagłówek i wiadomość po złożeniu zamówienia" accent="primary" />
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Nagłówek</label>
                <input
                  type="text"
                  value={form.thankYouPage.customHeading}
                  onChange={(e) => updateThankYou('customHeading', e.target.value)}
                  placeholder="Dziękujemy za zamówienie!"
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">Wiadomość</label>
                <textarea
                  value={form.thankYouPage.customMessage}
                  onChange={(e) => updateThankYou('customMessage', e.target.value)}
                  placeholder="Opcjonalna wiadomość wyświetlana pod nagłówkiem..."
                  rows={3}
                  className="input w-full resize-none"
                />
              </div>
            </div>
          </div>

          <div className="card">
            <SectionHeader icon={Layers} title="Elementy strony" description="Co pokazać na stronie podziękowania" accent="secondary" />
            <div className="space-y-3">
              <Toggle
                label="Szczegóły zamówienia"
                description="Pokaż podsumowanie zamówienia"
                checked={form.thankYouPage.showOrderDetails}
                onChange={(v) => updateThankYou('showOrderDetails', v)}
              />
              <Toggle
                label="Polecane produkty"
                description="Wyświetlaj produkty, które mogą zainteresować klienta"
                checked={form.thankYouPage.showRecommendedProducts}
                onChange={(v) => updateThankYou('showRecommendedProducts', v)}
              />
              {form.thankYouPage.showRecommendedProducts && (
                <SliderCard
                  label="Liczba polecanych produktów"
                  value={form.thankYouPage.recommendedProductsCount}
                  onChange={(v) => updateThankYou('recommendedProductsCount', v)}
                  min={2}
                  max={6}
                />
              )}
              <Toggle
                label="Przycisk powrotu do sklepu"
                description="Link do kontynuowania zakupów"
                checked={form.thankYouPage.showReturnToShopButton}
                onChange={(v) => updateThankYou('showReturnToShopButton', v)}
              />
              {form.thankYouPage.showReturnToShopButton && (
                <div className="p-4 rounded-lg border border-border-subtle bg-surface-raised">
                  <label className="block text-sm font-medium text-text-primary mb-2">URL powrotu</label>
                  <input
                    type="text"
                    value={form.thankYouPage.returnToShopUrl}
                    onChange={(e) => updateThankYou('returnToShopUrl', e.target.value)}
                    placeholder="Domyślny (strona główna sklepu)"
                    className="input w-full"
                  />
                </div>
              )}
              <Toggle
                label="Newsletter"
                description="Formularz zapisu do newslettera"
                checked={form.thankYouPage.showNewsletter}
                onChange={(v) => updateThankYou('showNewsletter', v)}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab !== 'general' && (
        <SettingsSaveBar saving={saving} status={saveStatus} onSave={handleSave} label="Zapisz wygląd stron" />
      )}

      {showPageBannerMediaPicker && adapter.isTenantScoped && (
        <WpMediaPickerModal
          integrationId={integrationId}
          onSelect={(url) => {
            setForm((current) => ({
              ...current,
              pages: {
                ...current.pages,
                bannerBackgroundImageUrl: url,
                bannerBackgroundType: 'image',
              },
            }));
            setShowPageBannerMediaPicker(false);
          }}
          onClose={() => setShowPageBannerMediaPicker(false)}
        />
      )}
    </div>
  );
}
